import { formatVND, formatDate, formatDateTime, formatNumber } from '../../../../utils/format';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { getSupplyHistoriesBySupply, getFarmersByHtx, getSeasonsByHtx } from '../../../../store';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { useAuth } from '../../../../hooks/useAuth';
import type { SupplyItem } from '../../../../types';

interface SupplyDetailModalProps {
  item: SupplyItem | null;
  onClose: () => void;
}

export function SupplyDetailModal({ item, onClose }: SupplyDetailModalProps) {
  useModalBackHandler(true, onClose);
  const { user } = useAuth();
  if (!item) return null;
  const histories = getSupplyHistoriesBySupply(item.id);
  const farmers = getFarmersByHtx(user?.htxId);
  const seasons = getSeasonsByHtx(user?.htxId);

  const getFarmerName = (id?: string) => farmers.find((f) => f.id === id)?.name || id || '-';
  const getSeasonName = (id?: string) => seasons.find((s) => s.id === id)?.name || id || '-';

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{item.name}</h3>
            <p className="text-xs text-slate-400">{item.maVatTu} · {item.nhom}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          <div className="flex items-center gap-3">
            <StatusBadge status={item.status} />
            {item.ngưỡngCảnhBáo && (
              <span className="text-xs text-slate-500">Ngưỡng cảnh báo: {item.ngưỡngCảnhBáo} {item.unit}</span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{formatNumber(item.stock)}</p>
              <p className="text-xs text-slate-500 mt-1">Tồn kho ({item.unit})</p>
            </div>
            <div className="bg-agrida-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-agrida-700">{formatNumber(item.available)}</p>
              <p className="text-xs text-agrida-600 mt-1">Khả dụng ({item.unit})</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{formatNumber(item.reserved)}</p>
              <p className="text-xs text-amber-600 mt-1">Đã cấp ({item.unit})</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-400 mb-1">Giá nhập</p>
              <p className="font-medium text-slate-900">{item.giaNhap ? formatVND(item.giaNhap) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Giá cung ứng</p>
              <p className="font-medium text-slate-900">{item.giaCungUng ? formatVND(item.giaCungUng) : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Nhà cung cấp</p>
              <p className="font-medium text-slate-900">{item.nhaCungCap || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Lô hàng</p>
              <p className="font-medium text-slate-900">{item.loHang || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Hạn sử dụng</p>
              <p className="font-medium text-slate-900">{formatDate(item.hanSuDung || '') || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-1">Giá trị tồn kho</p>
              <p className="font-medium text-slate-900">{item.giaNhap ? formatVND(item.stock * item.giaNhap) : '—'}</p>
            </div>
          </div>

          {item.ghiChu && (
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-400 mb-1">Ghi chú</p>
              <p className="text-sm text-slate-700">{item.ghiChu}</p>
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Lịch sử nhập/xuất ({histories.length})</h4>
            {histories.length === 0 ? (
              <p className="text-sm text-slate-400">Chưa có lịch sử nhập/xuất</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {histories.sort((a, b) => new Date(b.ngay).getTime() - new Date(a.ngay).getTime()).map((h) => (
                  <div key={h.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      h.loai === 'Nhập' ? 'bg-agrida-100 text-agrida-700' : h.loai === 'Xuất' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
                    }`}>
                      <i className={h.loai === 'Nhập' ? 'ri-arrow-down-line' : h.loai === 'Xuất' ? 'ri-arrow-up-line' : 'ri-settings-3-line'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900">
                          {h.loai === 'Nhập' ? `Nhập ${formatNumber(h.soLuong)}` : h.loai === 'Xuất' ? `Xuất ${formatNumber(h.soLuong)}` : `Điều chỉnh`} {item.unit}
                        </span>
                        <span className="text-xs text-slate-400">{formatDate(h.ngay)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{h.lyDo}</p>
                      {h.nongHoId && (
                        <p className="text-xs text-slate-400">Nông hộ: {getFarmerName(h.nongHoId)}</p>
                      )}
                      {h.hinhThuc && (
                        <p className="text-xs text-slate-400">Hình thức: {h.hinhThuc}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-0.5">Thực hiện: {h.nguoiThucHien}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}